/**
 * HTML → plain text.
 *
 * Not just `textContent` — that jams every block together on one line. This
 * keeps paragraph breaks, list bullets, and heading separation so the result
 * still reads like the original document.
 */

import { parseToBody } from './sanitize';

const BLOCK_TAGS = new Set([
  'address', 'article', 'aside', 'blockquote', 'dd', 'div', 'dl', 'dt',
  'fieldset', 'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3',
  'h4', 'h5', 'h6', 'header', 'hr', 'main', 'nav', 'ol', 'p', 'pre',
  'section', 'table', 'tbody', 'tfoot', 'thead', 'tr', 'ul',
]);

interface Ctx {
  listStack: Array<{ ordered: boolean; index: number }>;
  /** Keep exact whitespace (inside `<pre>`). */
  raw: boolean;
}

export function htmlToText(html: string): string {
  const body = parseToBody(html);
  const out = walk(Array.from(body.childNodes), { listStack: [], raw: false });
  return out
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim() + '\n';
}

function walk(nodes: Node[], ctx: Ctx): string {
  let out = '';
  for (const node of nodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const data = (node as Text).data;
      out += ctx.raw ? data : data.replace(/\s+/g, ' ');
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue;

    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    const kids = Array.from(el.childNodes);

    switch (tag) {
      case 'br':
        out += '\n';
        break;

      case 'hr':
        out += '\n\n----------\n\n';
        break;

      case 'pre':
        out += '\n' + (el.textContent ?? '').replace(/\n+$/, '') + '\n\n';
        break;

      case 'ul': case 'ol': {
        const ordered = tag === 'ol';
        const start = Number(el.getAttribute('start') ?? '1') || 1;
        const nested = { listStack: [...ctx.listStack, { ordered, index: start }], raw: ctx.raw };
        // Only the <li> children matter — the whitespace between them would
        // otherwise indent every item after the first.
        out += '\n' + walk(Array.from(el.children), nested) + '\n';
        break;
      }

      case 'li': {
        const level = Math.max(0, ctx.listStack.length - 1);
        const frame = ctx.listStack[level];
        const indent = '  '.repeat(level);
        const marker = frame?.ordered ? `${frame.index++}.` : '*';
        const text = walk(kids, ctx).trim();
        // Keep wrapped lines lined up under the first one.
        const hanging = text.split('\n').map((l, i) => (i === 0 ? l : `${indent}   ${l}`)).join('\n');
        out += `${indent}${marker} ${hanging}\n`;
        break;
      }

      case 'a': {
        const href = el.getAttribute('href');
        const text = walk(kids, ctx).trim();
        // Keep the destination when the link text doesn't already show it.
        out += href && href !== text && !/^#/.test(href) ? `${text} (${href})` : text;
        break;
      }

      case 'img': {
        const alt = el.getAttribute('alt');
        out += alt ? `[${alt}]` : '';
        break;
      }

      case 'td': case 'th':
        out += walk(kids, ctx).trim() + '\t';
        break;

      case 'tr':
        out += walk(Array.from(el.children), ctx).replace(/\t$/, '') + '\n';
        break;

      case 'table': case 'thead': case 'tbody': case 'tfoot':
        out += '\n' + walk(Array.from(el.children), ctx).trim() + '\n';
        break;

      case 'h1': case 'h2': case 'h3': case 'h4': case 'h5': case 'h6':
        out += '\n\n' + walk(kids, ctx).trim() + '\n\n';
        break;

      default:
        if (BLOCK_TAGS.has(tag)) out += '\n' + walk(kids, ctx).trim() + '\n';
        else out += walk(kids, ctx);
        break;
    }
  }
  return out;
}
